import React, {useRef, useState} from "react";
import {resolveObjectPath} from "../../functions";
import {Image} from 'react-img-placeholder';
import {Form} from "react-bootstrap";
import Icon from "@mdi/react";
import {mdiChevronDown, mdiChevronRight} from "@mdi/js";
import TableCell from "./TableCell";
import DateTimeTableCell from "./CellRenderer/DateTimeTableCell";
import { Button,ButtonGroup, OverlayTrigger, Tooltip } from "react-bootstrap";
/**
 * Renders a table row component based on the provided props.
 *
 * @param {object} props - The properties for the TableRow component.
 * @param {object} props.data - The data object used to populate the table row.
 * @param {array} props.colDefinitions - An array of column definitions for the table row.
 * @param {array} [props.actions] - An array of action objects, each containing an icon component, alt text, and callback function.
 *                                 When provided, these actions will be displayed in an overlay when hovering over the row.
 * @param {JSX.Element} props.actions[].icon - The icon component to display for the action.
 * @param {string} props.actions[].alt - The alt text for the action (used as title attribute).
 * @param {function} props.actions[].callback - The callback function to call when the action is clicked. Receives the row data as parameter.
 *
 * @return {JSX.Element} - The table row rendered as a <tr> element.
 */
export default function TableRow({
                                     data,
                                     index,
                                     draggable,
                                     colDefinitions,
                                     onRowClick,
                                     onRowSelect,
                                     onDragStart,
                                     onDragEnter,
                                     onDragLeave,
                                     onDragEnd,
                                     onDrag,
                                     onDrop,
                                     onDragOver,
                                     selectable,
                                     selected,
                                     showCheckbox,
                                     childRows,
                                     childRowContent,
                                     childRowState,
                                     customRowStyle=(() => ({})),
                                     customRowClass=(() => ([])),
                                     onChildRowStateChange,
                                     customCellRendererMap, // Standardmäßig TableCell verwenden
                                     hiddenCols,
                                     actions, // Array of {icon, alt, callback}
                                     actionsDisplay = 'overlay'
                                 }) {
    const [isHovering, setIsHovering] = useState(false);
    const hoverTimeoutRef = useRef(null);

    const rowStyle = {
        ...customRowStyle(data),
        ...(onRowClick ? {"cursor": "pointer"} : {}),
        position: 'relative' // For positioning the actions overlay
    };
    const customRowClassData = customRowClass(data);
    let trClass = Array.isArray(customRowClassData) ? customRowClassData : [customRowClassData];
    if (selectable && selected){
        trClass.push("selected");
    }
    let childRow = null;

// Check if the array colDefinitions contains an element with "key" attribute set to true
    const keyElement = colDefinitions.find(col => col.key === true);
// Save the id field of the element into keyField, else set it to "id"
    const keyField = keyElement ? keyElement.id : 'id';


    // Event handler to prevent the click event from bubbling up to the tr
    const handleCheckboxTdClick = (event) => {
        event.stopPropagation();
    };

    const handleCheckboxChange = (event) => {
        onRowSelect(event.target.checked);
    };

    const handleChildRowChange = () => {
        event.stopPropagation();
        if (childRowState) {
            onChildRowStateChange(-1);
        } else {
            onChildRowStateChange(data[keyField]);
        }
    }

    const handleMouseEnter = () => {
        if (actionsDisplay === 'overlay' && actions && actions.length > 0) {
            // Clear any existing timeout
            if (hoverTimeoutRef.current) {
                clearTimeout(hoverTimeoutRef.current);
            }
            // Set a new timeout to show the actions after a delay
            hoverTimeoutRef.current = setTimeout(() => {
                setIsHovering(true);
            }, 500); // 500ms delay before showing actions
        }
    };

    const handleMouseLeave = () => {
        // Clear the timeout if the mouse leaves before it triggers
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
            hoverTimeoutRef.current = null;
        }
        setIsHovering(false);
    };

    const handleActionClick = (callback, event) => {
        event.stopPropagation();
        callback(data);
    };

    let virt_col_index = 0;

    let actionOverlay = null;

// Überprüft, ob actions eine Funktion oder ein Array ist
    const resolvedActions = typeof actions === 'function' ? actions(data) : actions;

    if (actionsDisplay === 'overlay' && isHovering && resolvedActions && resolvedActions.length > 0) {
        actionOverlay = (
            <div className="row-actions-overlay-wrapper">
                <ButtonGroup className={"row-actions-overlay"} aria-label="Basic example">
                    {resolvedActions.map((action, actionIndex) => (
                        <OverlayTrigger
                            delay={{ show: 1250, hide: 400 }}
                            overlay={<Tooltip>{action.alt}</Tooltip>}
                            key={actionIndex}>
                            <Button
                                variant="primary"
                                onClick={(e) => handleActionClick(action.callback, e)}
                            >
                                {action.icon}
                            </Button>
                        </OverlayTrigger>
                    ))}
                </ButtonGroup>
            </div>
        );
    }

    return (<>
            <tr className={trClass.join(' ')} key={index} data-id={index} draggable={draggable} style={rowStyle}
                onDragStart={onDragStart} onDragEnter={onDragEnter} onDragLeave={onDragLeave} onDragEnd={onDragEnd}
                onDrag={onDrag} onDrop={onDrop} onDragOver={onDragOver}
                onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}
                onClick={() => {
                    onRowClick && onRowClick(data)
                }}>


                {showCheckbox &&
                    <td className="border check-select" onClick={handleCheckboxTdClick} style={{textAlign: 'center'}}><Form.Check
                        checked={selected} onChange={handleCheckboxChange}/>{actionOverlay}</td>}


                {colDefinitions.map(({id, type, placeholder, enum: enumMap, hidden}, index) => {
                    if (hidden) return false;
                    if (hiddenCols && hiddenCols.includes(id)) return false;

                    const isFirstVisibleColumn = virt_col_index === 0;
                    const value = resolveObjectPath(data, id);
                    const displayValue = enumMap?.[value] ?? value;
                    const tdClick = childRows && virt_col_index === 0 ? handleChildRowChange : null;
                    const childSwitch = childRows && virt_col_index === 0 ?
                        <Icon onClick={handleChildRowChange} path={childRowState ? mdiChevronDown : mdiChevronRight} size={1}
                              style={{display: "inline-block"}}/> : ""
                    virt_col_index++;
                    switch (type) {
                        case 'image':
                            const src = displayValue ? displayValue : placeholder;
                            return (<td onClick={tdClick} key={id} className={"border px-4 py-2 " + trClass.join(' ')+" "+(isFirstVisibleColumn ? "text-nowrap" : "")}>{isFirstVisibleColumn && actionOverlay} {childSwitch} <Image
                                src={src}
                                alt="prouct image"
                                width={50}
                                height={50}
                                placeholdersrc={placeholder} // <- this field is optional

                            /></td>);
                            break;
                        case 'date':
                            return (<DateTimeTableCell id={id} onClick={tdClick} className={isFirstVisibleColumn ? "text-nowrap" : ""} rowData={data} childSwitch={(<>{isFirstVisibleColumn && actionOverlay} {childSwitch}</>)}>{displayValue}</DateTimeTableCell>)
                            break;
                        default:
                            let CustomCell = TableCell;
                            if (customCellRendererMap) {
                                if (customCellRendererMap[id]) {
                                    CustomCell = customCellRendererMap[id];
                                } else if (customCellRendererMap['_default']) {
                                    CustomCell = customCellRendererMap['_default'];
                                }
                            }
                            return (<CustomCell id={id} onClick={tdClick} className={isFirstVisibleColumn ? "text-nowrap" : ""} childSwitch={(<>{isFirstVisibleColumn && actionOverlay} {childSwitch}</>)} rowData={data}> {isFirstVisibleColumn && actionOverlay} {childSwitch} {displayValue}</CustomCell>);
                    }


                })}

                {actionsDisplay === 'column' && (
                    <td className="border px-2 py-2 actions-cell" style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                        {resolvedActions && resolvedActions.length > 0 ? (
                            <ButtonGroup aria-label="Row actions">
                                {resolvedActions.map((action, actionIndex) => (
                                    <OverlayTrigger
                                        delay={{ show: 750, hide: 200 }}
                                        overlay={<Tooltip>{action.alt}</Tooltip>}
                                        key={actionIndex}>
                                        <Button
                                            variant="link"
                                            className={"btn-table-action"}
                                            onClick={(e) => handleActionClick(action.callback, e)}
                                        >
                                            {action.icon}
                                        </Button>
                                    </OverlayTrigger>
                                ))}
                            </ButtonGroup>
                        ) : null}
                    </td>
                )}
            </tr>
            {childRowState && <tr>
                {(() => {
                    const visibleCols = colDefinitions.filter(({id, hidden}) => !hidden && !(hiddenCols && hiddenCols.includes(id))).length;
                    const extraCols = (showCheckbox ? 1 : 0) + (actionsDisplay === 'column' && resolvedActions && resolvedActions.length > 0 ? 1 : 0);
                    return <td className={"border px-4 py-2"} colSpan={visibleCols + extraCols}>{childRowContent}</td>
                })()}
            </tr>}
        </>
    );
}
